import { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { invoicesAPI } from '@/lib/api';
import { formatDate, cn } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  FileText, 
  Filter,
  Calendar,
  DollarSign,
  Send,
  CheckCircle,
  Clock,
  AlertTriangle,
  Eye,
  Pencil,
  X,
  Plus,
  Trash2,
  CreditCard,
  Building2,
  Mail,
  Phone,
  MapPin,
  Printer,
  ExternalLink
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export default function AdminInvoices() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Invoice detail state
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Invoice view/print state
  const [showInvoiceView, setShowInvoiceView] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await invoicesAPI.getAll(params);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await invoicesAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const response = await invoicesAPI.getById(invoice.id);
      setSelectedInvoice(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to load invoice details');
    }
  };

  const handleOpenPrintView = async (invoice) => {
    try {
      const response = await invoicesAPI.getById(invoice.id);
      setViewInvoice(response.data);
      setShowInvoiceView(true);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to load invoice');
    }
  };

  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;
    
    setSaving(true);
    try {
      await invoicesAPI.send(selectedInvoice.id);
      toast.success('Invoice marked as sent!');
      setShowDetailDialog(false);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;
    
    setSaving(true);
    try {
      await invoicesAPI.markPaid(selectedInvoice.id);
      toast.success('Invoice marked as paid!');
      setShowDetailDialog(false);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error marking paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLineItem = async (lineItem, updates) => {
    if (!selectedInvoice) return;
    
    setSaving(true);
    try {
      await invoicesAPI.updateLineItem(selectedInvoice.id, lineItem.id, updates);
      // Refresh invoice
      const response = await invoicesAPI.getById(selectedInvoice.id);
      setSelectedInvoice(response.data);
      setEditingLineItem(null);
      toast.success('Line item updated');
      fetchInvoices();
    } catch (error) {
      console.error('Error updating line item:', error);
      toast.error('Failed to update line item');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNotes = async (notes) => {
    if (!selectedInvoice) return;
    
    try {
      await invoicesAPI.update(selectedInvoice.id, { notes });
      setSelectedInvoice(prev => ({ ...prev, notes }));
      toast.success('Notes updated');
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  };

  const handleUpdatePayNowUrl = async (pay_now_url) => {
    if (!selectedInvoice) return;
    
    try {
      await invoicesAPI.update(selectedInvoice.id, { pay_now_url });
      setSelectedInvoice(prev => ({ ...prev, pay_now_url }));
      toast.success('Pay Now URL updated');
    } catch (error) {
      console.error('Error updating Pay Now URL:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700 border-slate-200',
      sent: 'bg-blue-100 text-blue-700 border-blue-200',
      paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      overdue: 'bg-red-100 text-red-700 border-red-200',
    };
    
    const icons = {
      draft: <Pencil size={12} className="mr-1" />,
      sent: <Send size={12} className="mr-1" />,
      paid: <CheckCircle size={12} className="mr-1" />,
      overdue: <AlertTriangle size={12} className="mr-1" />,
    };
    
    return (
      <Badge variant="outline" className={styles[status] || styles.draft}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <AdminLayout title="Invoices">
      <div className="space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-200 rounded-lg">
                    <FileText size={20} className="text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.counts.draft}</p>
                    <p className="text-xs text-slate-500">Draft</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-200 rounded-lg">
                    <Send size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-900">{stats.counts.sent}</p>
                    <p className="text-xs text-blue-600">Sent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-200 rounded-lg">
                    <CheckCircle size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-900">{stats.counts.paid}</p>
                    <p className="text-xs text-emerald-600">Paid</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-200 rounded-lg">
                    <AlertTriangle size={20} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-900">{stats.counts.overdue}</p>
                    <p className="text-xs text-red-600">Overdue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-200 rounded-lg">
                    <DollarSign size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-900">{formatCurrency(stats.amounts.outstanding)}</p>
                    <p className="text-xs text-amber-600">Outstanding</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-slate-400" />
            <span className="text-sm text-slate-600">Filter:</span>
          </div>
          <div className="flex gap-2">
            {['all', 'draft', 'sent', 'paid', 'overdue'].map((status) => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status ? 'bg-[#194461]' : ''}
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Invoices Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#52B8E1]" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="mx-auto mb-4 opacity-30" size={48} />
                <p className="text-lg">No invoices found</p>
                <p className="text-sm">Invoices are auto-generated when delivery jobs are completed.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} className="cursor-pointer hover:bg-slate-50">
                      <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                      <TableCell>{invoice.customer_name || 'Unknown'}</TableCell>
                      <TableCell>{formatDate(invoice.issue_date)}</TableCell>
                      <TableCell>{formatDate(invoice.due_date)}</TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(invoice.total_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewInvoice(invoice)}
                            data-testid={`view-invoice-${invoice.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenPrintView(invoice)}
                            data-testid={`print-invoice-${invoice.id}`}
                          >
                            <Printer size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText size={20} className="text-[#52B8E1]" />
                Invoice {selectedInvoice?.invoice_number}
              </span>
              {selectedInvoice && getStatusBadge(selectedInvoice.status)}
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Customer</h4>
                  <p className="font-medium text-slate-900">{selectedInvoice.customer_name}</p>
                  {selectedInvoice.customer_email && (
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <Mail size={12} />
                      {selectedInvoice.customer_email}
                    </p>
                  )}
                  {selectedInvoice.customer_phone && (
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <Phone size={12} />
                      {selectedInvoice.customer_phone}
                    </p>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Invoice Details</h4>
                  <p className="text-sm text-slate-600">Issue Date: {formatDate(selectedInvoice.issue_date)}</p>
                  <p className="text-sm text-slate-600">Due Date: {formatDate(selectedInvoice.due_date)}</p>
                  {selectedInvoice.site_address && (
                    <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                      <MapPin size={12} />
                      {selectedInvoice.site_address}
                    </p>
                  )}
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Line Items</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-20 text-center">Qty</TableHead>
                      <TableHead className="w-28 text-right">Unit Price</TableHead>
                      <TableHead className="w-28 text-right">Total</TableHead>
                      {selectedInvoice.status === 'draft' && (
                        <TableHead className="w-16"></TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInvoice.line_items?.map((item) => (
                      <TableRow key={item.id}>
                        {editingLineItem?.id === item.id ? (
                          <>
                            <TableCell>
                              <Input
                                value={editingLineItem.description}
                                onChange={(e) => setEditingLineItem({...editingLineItem, description: e.target.value})}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={editingLineItem.quantity}
                                onChange={(e) => setEditingLineItem({...editingLineItem, quantity: parseInt(e.target.value) || 1})}
                                className="h-8 w-16 text-center"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={editingLineItem.unit_price}
                                onChange={(e) => setEditingLineItem({...editingLineItem, unit_price: parseFloat(e.target.value) || 0})}
                                className="h-8 w-24 text-right"
                              />
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency((editingLineItem.quantity || 1) * (editingLineItem.unit_price || 0))}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-emerald-600"
                                  onClick={() => handleUpdateLineItem(item, {
                                    description: editingLineItem.description,
                                    quantity: editingLineItem.quantity,
                                    unit_price: editingLineItem.unit_price
                                  })}
                                  disabled={saving}
                                >
                                  <CheckCircle size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-slate-400"
                                  onClick={() => setEditingLineItem(null)}
                                >
                                  <X size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.line_total)}</TableCell>
                            {selectedInvoice.status === 'draft' && (
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-[#194461]"
                                  onClick={() => setEditingLineItem({...item})}
                                >
                                  <Pencil size={14} />
                                </Button>
                              </TableCell>
                            )}
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                <div className="flex justify-end mt-4 pt-4 border-t">
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(selectedInvoice.total_amount)}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.status === 'draft' && (
                <div>
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add notes to this invoice..."
                    value={selectedInvoice.notes || ''}
                    onChange={(e) => setSelectedInvoice({...selectedInvoice, notes: e.target.value})}
                    onBlur={(e) => handleUpdateNotes(e.target.value)}
                    rows={2}
                  />
                </div>
              )}

              {/* Pay Now URL */}
              {selectedInvoice.status === 'draft' && (
                <div>
                  <Label htmlFor="pay_now_url">Pay Now URL (for future Stripe integration)</Label>
                  <Input
                    id="pay_now_url"
                    placeholder="https://pay.stripe.com/..."
                    value={selectedInvoice.pay_now_url || ''}
                    onChange={(e) => setSelectedInvoice({...selectedInvoice, pay_now_url: e.target.value})}
                    onBlur={(e) => handleUpdatePayNowUrl(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            
            {selectedInvoice?.status === 'draft' && (
              <Button
                onClick={handleSendInvoice}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="send-invoice-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                Send Invoice
              </Button>
            )}
            
            {(selectedInvoice?.status === 'sent' || selectedInvoice?.status === 'overdue') && (
              <Button
                onClick={handleMarkPaid}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="mark-paid-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" />}
                Mark as Paid
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Print View Dialog */}
      <Dialog open={showInvoiceView} onOpenChange={setShowInvoiceView}>
        <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto p-0">
          {viewInvoice && (
            <div className="bg-white" id="invoice-print">
              {/* Invoice Header */}
              <div className="p-8 border-b">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <img src="/logo-icon.svg" alt="Tinkle Tent" className="h-12 w-12" />
                      <div>
                        <h1 className="text-2xl font-bold text-[#194461]">Tinkle Tent Rentals</h1>
                        <p className="text-sm text-slate-500">Johnson City, TN</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">info@tinkletent.com</p>
                    <p className="text-sm text-slate-600">(423) 555-0123</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-3xl font-bold text-slate-900">INVOICE</h2>
                    <p className="text-lg font-medium text-slate-700 mt-1">{viewInvoice.invoice_number}</p>
                    {getStatusBadge(viewInvoice.status)}
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="p-8 grid grid-cols-2 gap-8 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Bill To</h3>
                  <p className="font-semibold text-slate-900">{viewInvoice.customer_name}</p>
                  {viewInvoice.customer_address && (
                    <p className="text-slate-600">{viewInvoice.customer_address}</p>
                  )}
                  {viewInvoice.customer_email && (
                    <p className="text-slate-600">{viewInvoice.customer_email}</p>
                  )}
                  {viewInvoice.customer_phone && (
                    <p className="text-slate-600">{viewInvoice.customer_phone}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="inline-block text-left">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                      <p className="text-sm text-slate-500">Issue Date:</p>
                      <p className="text-sm font-medium">{formatDate(viewInvoice.issue_date)}</p>
                      <p className="text-sm text-slate-500">Due Date:</p>
                      <p className="text-sm font-medium">{formatDate(viewInvoice.due_date)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Site Address */}
              {viewInvoice.site_address && (
                <div className="px-8 py-4 bg-slate-50 border-b">
                  <p className="text-sm">
                    <span className="text-slate-500">Service Location: </span>
                    <span className="font-medium">{viewInvoice.site_address}</span>
                  </p>
                </div>
              )}

              {/* Line Items */}
              <div className="p-8">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-3 text-sm font-semibold text-slate-700">Description</th>
                      <th className="text-center py-3 text-sm font-semibold text-slate-700 w-20">Qty</th>
                      <th className="text-right py-3 text-sm font-semibold text-slate-700 w-28">Unit Price</th>
                      <th className="text-right py-3 text-sm font-semibold text-slate-700 w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewInvoice.line_items?.map((item, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="py-4 text-slate-900">{item.description}</td>
                        <td className="py-4 text-center text-slate-600">{item.quantity}</td>
                        <td className="py-4 text-right text-slate-600">{formatCurrency(item.unit_price)}</td>
                        <td className="py-4 text-right font-medium text-slate-900">{formatCurrency(item.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Total */}
                <div className="flex justify-end mt-6 pt-6 border-t-2 border-slate-200">
                  <div className="w-64">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-lg font-semibold text-slate-700">Total Due</span>
                      <span className="text-2xl font-bold text-[#194461]">{formatCurrency(viewInvoice.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {viewInvoice.notes && (
                <div className="px-8 pb-4">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Notes: </span>
                    {viewInvoice.notes}
                  </p>
                </div>
              )}

              {/* Footer */}
              <div className="p-8 bg-slate-50 border-t">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Terms:</span> Net 30. Please pay within 30 days of invoice date.
                    </p>
                    <p className="text-sm text-slate-500 mt-1">Thank you for your business!</p>
                  </div>
                  {viewInvoice.pay_now_url ? (
                    <a 
                      href={viewInvoice.pay_now_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#194461] text-white rounded-lg hover:bg-[#091D2E] transition-colors"
                    >
                      <CreditCard size={18} />
                      Pay Now
                      <ExternalLink size={14} />
                    </a>
                  ) : (
                    <Button disabled className="opacity-50">
                      <CreditCard size={18} className="mr-2" />
                      Pay Now (Coming Soon)
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="p-4 border-t bg-slate-50 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowInvoiceView(false)}>
              Close
            </Button>
            <Button 
              onClick={() => window.print()}
              className="bg-[#194461] hover:bg-[#091D2E]"
            >
              <Printer size={16} className="mr-2" />
              Print Invoice
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
